import { QUICK_ACTION_GLYPHS, GlyphChevron } from "./QuickActionGlyphs";

/**
 * QuickActionsPanel
 *
 * Vertical list of quick-action rows (icon bubble + two-line label),
 * matching the FemCare sidebar/quick-links pattern. Purely presentational:
 * pass parsed `data` (shape of quickActionsData.json) in as a prop.
 *
 * Drop this anywhere — sidebar, drawer, dashboard column, etc.
 *
 * @param {object} data                            - shape of quickActionsData.json
 * @param {(action: object) => void} [onActionClick] - called with the clicked action; falls back to <a href>
 * @param {() => void} [onExpandClick]              - if provided, renders the floating chevron button (e.g. to expand/scroll)
 * @param {string} [className]                      - extra classes for the outer card (e.g. to control width)
 */
export default function QuickActionsPanel({ data, onActionClick, onExpandClick, className = "" }) {
  if (!data) return null;

  const { actions = [] } = data;

  return (
    <div className={`relative w-full max-w-[210px] ${className}`}>
      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
        {actions.map((action, i) => (
          <QuickActionRow
            key={action.key}
            action={action}
            onClick={onActionClick}
            isLast={i === actions.length - 1}
          />
        ))}
      </div>

      {onExpandClick && (
        <button
          type="button"
          onClick={onExpandClick}
          aria-label="Show more actions"
          className="absolute right-[-14px] top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-gray-900 text-white shadow-lg transition-colors hover:bg-gray-800"
        >
          <GlyphChevron size={15} />
        </button>
      )}
    </div>
  );
}

function QuickActionRow({ action, onClick, isLast }) {
  const Glyph = QUICK_ACTION_GLYPHS[action.iconKey];
  const tint = TINT_CLASSES[action.tint] || TINT_CLASSES.violet;

  return (
    <a
      href={action.href || "#"}
      onClick={
        onClick
          ? (e) => {
              e.preventDefault();
              onClick(action);
            }
          : undefined
      }
      className={`flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-gray-50 ${
        isLast ? "" : "border-b border-gray-100"
      }`}
    >
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${tint.bg}`}>
        {Glyph && <Glyph size={20} className={tint.fg} />}
      </span>
      <span className="text-sm font-semibold leading-tight text-gray-800">{action.label}</span>
    </a>
  );
}

/**
 * Static Tailwind class map — keys must match the "tint" values in
 * quickActionsData.json. Kept static so Tailwind's purge step can see them.
 */
const TINT_CLASSES = {
  rose: { bg: "bg-rose-50", fg: "text-rose-500" },
  violet: { bg: "bg-violet-50", fg: "text-violet-500" },
  indigo: { bg: "bg-indigo-50", fg: "text-indigo-500" },
  emerald: { bg: "bg-emerald-50", fg: "text-emerald-500" },
  blue: { bg: "bg-blue-50", fg: "text-blue-500" },
  red: { bg: "bg-red-50", fg: "text-red-500" },
};